/**
 * ImageUpload Component
 * Allows users to upload images with preview, compression, and progress indication
 * [UPDATED v0.7.2] Added client-side image compression
 */

import React, { useState, useRef, useCallback } from 'react';
import { uploadImage } from '../../../services/imagesApi';
import type { ImageUploadResponse } from '../../../services/imagesApi';
import { compressImage, formatFileSize } from '../../../utils/imageCompression';
import './ImageUpload.css';

interface ImageUploadProps {
  entityType: 'ad' | 'wish' | 'member';
  entityId: string;
  onUploadComplete?: (images: ImageUploadResponse[]) => void;
  maxImages?: number;
  currentImageCount?: number;
  existingImages?: string[];
  onImageDelete?: (imageUrl: string) => void;
  enableCompression?: boolean; // [NEW] Enable/disable client-side compression
}

interface UploadingImage {
  file: File;
  compressedFile?: File; // [NEW] Compressed file
  progress: number;
  status: 'uploading' | 'compressing' | 'success' | 'error';
  response?: ImageUploadResponse;
  error?: string;
  originalSize?: number; // [NEW] Original file size
  compressedSize?: number; // [NEW] Compressed file size
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  entityType,
  entityId,
  onUploadComplete,
  maxImages = 10,
  currentImageCount = 0,
  existingImages = [],
  onImageDelete,
  enableCompression = true, // [NEW] Default to true
}) => {
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [notifiedUrls, setNotifiedUrls] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canUpload = currentImageCount + uploadingImages.length < maxImages;

  const handleFileSelect = useCallback(async (files: FileList) => {
    const imageFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      return;
    }

    const availableSlots = maxImages - currentImageCount - uploadingImages.length;
    const filesToUpload = imageFiles.slice(0, availableSlots);

    const newUploadingImages: UploadingImage[] = filesToUpload.map(file => ({
      file,
      progress: 0,
      status: enableCompression ? 'compressing' : 'uploading',
      originalSize: file.size,
    }));

    setUploadingImages(prev => [...prev, ...newUploadingImages]);

    // Process each image: compress (if enabled) then upload
    for (let i = 0; i < newUploadingImages.length; i++) {
      const uploadingImage = newUploadingImages[i];
      const imageIndex = uploadingImages.length + i;

      try {
        let fileToUpload = uploadingImage.file;

        // Step 1: Compress image if enabled
        if (enableCompression) {
          try {
            const compressed = await compressImage(uploadingImage.file, {
              maxWidth: 1920,
              quality: 0.85,
            });

            fileToUpload = compressed.file;

            // Update with compression results
            setUploadingImages(prev =>
              prev.map((img, idx) =>
                idx === imageIndex
                  ? {
                      ...img,
                      compressedFile: compressed.file,
                      compressedSize: compressed.compressedSize,
                      status: 'uploading',
                    }
                  : img
              )
            );
          } catch (compressionError) {
            // If compression fails, use original file
            console.warn('Compression failed, using original file:', compressionError);
            setUploadingImages(prev =>
              prev.map((img, idx) =>
                idx === imageIndex
                  ? { ...img, status: 'uploading' }
                  : img
              )
            );
          }
        }

        // Step 2: Upload image
        const response = await uploadImage({
          file: fileToUpload,
          entityType,
          entityId,
        });

        if (response.success && response.data) {
          const uploadedUrl = response.data!.url;
          setUploadedUrls(prev => [...prev, uploadedUrl]);

          setUploadingImages(prev =>
            prev.map((img, idx) =>
              idx === imageIndex
                ? { ...img, progress: 100, status: 'success', response: response.data! }
                : img
            )
          );
        } else {
          throw new Error(response.error?.message || '上傳失敗');
        }
      } catch (error) {
        setUploadingImages(prev =>
          prev.map((img, idx) =>
            idx === imageIndex
              ? { ...img, progress: 0, status: 'error', error: error instanceof Error ? error.message : '上傳失敗' }
              : img
          )
        );
      }
    }
  }, [entityType, entityId, currentImageCount, uploadingImages.length, maxImages, enableCompression]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!canUpload) return;
    handleFileSelect(e.dataTransfer.files);
  }, [canUpload, handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && canUpload) {
      handleFileSelect(e.target.files);
    }
  }, [canUpload, handleFileSelect]);

  const handleClick = () => {
    if (canUpload && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeUploadingImage = (index: number) => {
    setUploadingImages(prev => prev.filter((_, idx) => idx !== index));
  };

  const completedImages = uploadingImages.filter(img => img.status === 'success');

  React.useEffect(() => {
    if (completedImages.length > 0 && onUploadComplete) {
      const responses = completedImages
        .map(img => img.response!)
        .filter(Boolean);

      // Only notify for URLs we haven't notified yet
      const newResponses = responses.filter(r => !notifiedUrls.has(r.url));
      if (newResponses.length > 0) {
        onUploadComplete(newResponses);
        setNotifiedUrls(prev => new Set([...prev, ...newResponses.map(r => r.url)]));
      }
      // Don't clear - wait for parent to update existingImages
    }
  }, [completedImages, onUploadComplete, notifiedUrls]);

  // Add new effect to sync with parent
  React.useEffect(() => {
    const uploadedInParent = uploadedUrls.filter(url => existingImages.includes(url));
    if (uploadedInParent.length > 0) {
      setUploadedUrls(prev => prev.filter(url => !existingImages.includes(url)));
      setUploadingImages(prev => prev.filter(img => img.status !== 'success'));
      // Also clear notified URLs for images that are now in existingImages
      setNotifiedUrls(prev => {
        const next = new Set(prev);
        uploadedInParent.forEach(url => next.delete(url));
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingImages]);

  const totalImageCount = currentImageCount + existingImages.length + uploadedUrls.length;

  return (
    <div className="image-upload">
      <div className="image-upload-header">
        <span className="image-upload-count">
          圖片 {totalImageCount} / {maxImages}
        </span>
        <span className="image-upload-hint">
          {enableCompression
            ? '支援 JPG、PNG、WebP 格式，自動壓縮至 1920px'
            : '支援 JPG、PNG、WebP 格式，最大 5MB'}
        </span>
      </div>

      <div
        className={`image-upload-dropzone ${!canUpload ? 'disabled' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        <div className="image-upload-dropzone-content">
          <svg
            className="image-upload-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p>拖放圖片到這裡，或點擊選擇</p>
          {!canUpload && (
            <p className="image-upload-limit-message">已達到圖片數量上限</p>
          )}
        </div>
      </div>

      {(uploadingImages.length > 0 || existingImages.length > 0 || uploadedUrls.length > 0) && (
        <div className="image-upload-preview">
          {existingImages.map((imageUrl, index) => (
            <div key={`existing-${index}`} className="image-upload-preview-item">
              <img src={imageUrl} alt={`Preview ${index + 1}`} />
              {onImageDelete && (
                <button
                  type="button"
                  className="image-upload-remove"
                  onClick={() => onImageDelete(imageUrl)}
                  aria-label="Remove image"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          {uploadedUrls.map((url, index) => (
            <div key={`uploaded-${index}`} className="image-upload-preview-item">
              <img src={url} alt={`Uploaded ${index + 1}`} />
              <div className="image-upload-success">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          ))}

          {uploadingImages
            .map((img, originalIndex) => ({ img, originalIndex }))
            .filter(({ img }) => img.status !== 'success')
            .map(({ img: uploadingImage, originalIndex }) => (
              <div key={`uploading-${originalIndex}`} className="image-upload-preview-item">
                <img src={URL.createObjectURL(uploadingImage.file)} alt={`Uploading ${originalIndex + 1}`} />

              {/* Compression status */}
              {uploadingImage.status === 'compressing' && (
                <div className="image-upload-compressing">
                  <div className="image-upload-compressing-spinner"></div>
                  <span className="image-upload-compressing-text">壓縮中...</span>
                </div>
              )}

              {/* Upload progress */}
              {uploadingImage.status === 'uploading' && (
                <div className="image-upload-progress">
                  <div className="image-upload-progress-bar" style={{ width: `${uploadingImage.progress}%` }} />
                </div>
              )}

              {/* Success with compression info */}
              {uploadingImage.status === 'success' && (
                <div className="image-upload-success">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {enableCompression && uploadingImage.originalSize && uploadingImage.compressedSize && (
                    <span className="image-upload-compression-info">
                      {formatFileSize(uploadingImage.originalSize)} → {formatFileSize(uploadingImage.compressedSize)}
                    </span>
                  )}
                </div>
              )}

              {/* Error */}
              {uploadingImage.status === 'error' && (
                <div className="image-upload-error">
                  <div className="image-upload-error-message">{uploadingImage.error}</div>
                  <button
                    type="button"
                    className="image-upload-remove"
                    onClick={() => removeUploadingImage(originalIndex)}
                    aria-label="Remove failed upload"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
